import React, { useCallback, useMemo, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  InputBase,
  Popover,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import axios from "axios";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MicIcon from "@mui/icons-material/Mic";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import GppGoodIcon from "@mui/icons-material/GppGood";
import { DEFAULT_CHAT_STARTER } from "./chatSeed";

import { StreamSummaryBanner } from "../../components/immersive-panel/StreamSummaryBanner";
import type { FeatureDefinition } from "../immersive-panel-setup/ImmersivePanelSetupPage";


const API_BASE = process.env.REACT_APP_AI_API_URL || "http://127.0.0.1:8000";
// const API_BASE = "http://127.0.0.1:8000";
const RUNTIME_WS_BASE = "wss://api.staging-new.boltplus.tv/ai";

type RuntimeAnnouncement = {
  text: string;
  timestamp?: number;
};

type RuntimeQuiz = {
  question: string;
  options: string[];
  timestamp?: number;
};

type Props = {
  features: FeatureDefinition[];
  activeIds: string[];
  sessionKey?: string;
  initialChat?: RuntimeChatMessage[];
  initialAnnouncements?: RuntimeAnnouncement[];
  initialQuizzes?: RuntimeQuiz[];
};

type PanelConfig = {
  showChat: boolean;
  showAI: boolean;
  showShop: boolean;
  showQuiz: boolean;
  showAnnouncement: boolean;
  showAd: boolean;
  showAiEngagement: boolean;
  showModeration: boolean;
  showVoice: boolean;
};

export const PANEL_WIDTH = 460;
export const PANEL_HEIGHT = 900;

const HeaderTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 700,
  fontSize: 15,
  borderRadius: 10,
  color: alpha("#FFFFFF", 0.75),
  minHeight: 46,
  minWidth: 0,
  padding: "6px 22px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontFamily: "'Inter', sans-serif",
  "&:first-of-type": { marginLeft: theme.spacing(3.5) },
  "&.Mui-selected": {
    background:
      "linear-gradient(135deg, rgba(107,79,255,0.65) 0%, rgba(139,92,246,0.85) 100%)",
    color: "#FFFFFF",
    boxShadow: "0 8px 20px rgba(107,79,255,0.35)",
  },
  "&:hover": { color: "#FFFFFF", background: alpha("#6B4FFF", 0.25) },
}));

const SCROLLBAR_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#7C3AED transparent",
  "&::-webkit-scrollbar": { width: 10 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "linear-gradient(180deg, #7C3AED 0%, #6366F1 100%)",
    borderRadius: 999,
    border: "2px solid rgba(5,7,22,0.5)",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "linear-gradient(180deg, #8B5CF6 0%, #818CF8 100%)",
  },
};

const EMOJI_CHOICES = [
  "\u{1F600}",
  "\u{1F602}",
  "\u{1F60D}",
  "\u{1F525}",
  "\u{1F44F}",
  "\u{1F914}",
  "\u{1F64C}",
  "\u{1F389}",
  "\u{1F622}",
  "\u{1F60E}",
];

type RuntimeChatMessage = {
  author: string;
  text: string;
  avatar: string;
  timestamp?: number;
};

type DisplayChatMessage = RuntimeChatMessage & {
  ai?: boolean;
  __moderated?: boolean;
  reason?: string;
};


const DEFAULT_ANNOUNCEMENT_COPY = "The stream just captured an emotional moment as we witnessed beautiful trees being taken down, sparking a deep reflection on spirituality and connection to nature.";
const AvatarBubble: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      position: "relative",
      width: 36,
      height: 36,
      borderRadius: "50%",
      flexShrink: 0,
      "&::before": {
        content: '""',
        position: "absolute",
        inset: -2,
        borderRadius: "50%",
        background:
          "linear-gradient(135deg, rgba(107,79,255,0.9), rgba(84,46,255,0.9))",
        filter: "blur(4px)",
        opacity: 0.6,
      },
      display: "grid",
      placeItems: "center",
      background:
        "linear-gradient(135deg, rgba(107,79,255,0.75) 0%, rgba(84,46,255,0.9) 100%)",
      border: "1px solid rgba(255,255,255,0.22)",
      color: "#FFFFFF",
      fontWeight: 700,
      letterSpacing: 0.2,
    }}
  >
    {label}
  </Box>
);

type AiMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
};

const normaliseChatMessages = (incoming?: RuntimeChatMessage[]): DisplayChatMessage[] => {
  const base = incoming && incoming.length ? incoming : DEFAULT_CHAT_STARTER;
  const now = Date.now() / 1000;
  return base.map((item, index) => ({
    author: item.author,
    text: item.text,
    avatar: item.avatar,
    timestamp: item.timestamp ?? now + index,
    ai: (item as DisplayChatMessage).ai,
    __moderated: (item as DisplayChatMessage).__moderated,
    reason: (item as DisplayChatMessage).reason,
  }));
};

const AiMessageBubble: React.FC<{ messageRole: AiMessage["role"]; text: string; loading?: boolean }> = ({
  messageRole,
  text,
  loading,
}) => {
  const isUser = messageRole === "user";
  return (
    <Box
      sx={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
        borderRadius: 3,
        px: 2.25,
        py: 1.5,
        background: isUser
          ? "linear-gradient(135deg, rgba(124,58,237,0.85), rgba(99,102,241,0.85))"
          : alpha("#1E213F", 0.85),
        color: "#FFFFFF",
        boxShadow: isUser ? "0 8px 18px rgba(99,102,241,0.35)" : "none",
        border: isUser ? "none" : `1px solid ${alpha("#FFFFFF", 0.08)}`,
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
      }}
    >
      {!isUser && (
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "linear-gradient(135deg, rgba(107,79,255,0.65), rgba(139,92,246,0.85))",
            display: "grid",
            placeItems: "center",
            fontSize: 10,
            fontWeight: 700,
            color: "#FFFFFF",
            flexShrink: 0,
            mt: 0.45,
          }}
        >
          AI
        </Box>
      )}
      <Typography
        variant="body2"
        sx={{
          color: "#FFFFFF",
          opacity: loading ? 0.7 : 1,
          fontStyle: loading ? "italic" : "normal",
          lineHeight: 1.6,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};
const QuizCard: React.FC<{ quiz?: RuntimeQuiz | null }> = ({ quiz }) => {
  const question =
    quiz?.question ??
    "Which group is working to protect the rainforest and their way of life by keeping roads out?";

  const options =
    (quiz?.options?.length ? quiz.options : [
      "Nantu and his people",
      "Friends of the Earth",
      "Amazon Conservation Team",
      "The World Wildlife Fund",
    ]).map((label, i) => ({
      label,
      key: String.fromCharCode(65 + i), // A, B, C, D…
    }));

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha("#6B4FFF", 0.38)}`,
        background: alpha("#6B4FFF", 0.10),
        p: 1.75,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      {/* header */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="overline" sx={{ color: "#A5B4FC", fontWeight: 800, letterSpacing: 1.2, lineHeight: 1 }}>
          LIVE QUIZ
        </Typography>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#A5B4FC" }} />
      </Stack>

      {/* question */}
      <Typography variant="body2" sx={{ color: "#FFFFFF", fontWeight: 600, lineHeight: 1.5 }}>
        {question}
      </Typography>

      {/* 2×2 grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 0.75,
          mt: 0.25,
        }}
      >
        {options.map((opt) => (
          <Button
            key={opt.key}
            variant="outlined"
            size="small"
            fullWidth
            startIcon={
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 800,
                  lineHeight: 1,
                  color: "#FFFFFF",
                  background:
                    "linear-gradient(135deg, rgba(124,58,237,0.9), rgba(99,102,241,0.95))",
                  border: `1px solid ${alpha("#FFFFFF", 0.18)}`,
                  boxShadow:
                    "0 2px 8px rgba(124,58,237,0.35), inset 0 0 0 1px rgba(255,255,255,0.12)",
                  flexShrink: 0,
                }}
              >
                <Box component="span" sx={{ position: "relative", top: "0.5px" }}>
                  {opt.key}
                </Box>
              </Box>
            }
            sx={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
              textAlign: "left",
              borderRadius: 2,
              borderColor: alpha("#FFFFFF", 0.12),
              color: "#E0E7FF",
              textTransform: "none",
              fontWeight: 600,
              px: 1.25,
              py: 1,
              height: "auto",
              minHeight: 38,
              "& .MuiButton-startIcon": { mr: 1, mt: 0.25 },
              "&:hover": { borderColor: alpha("#6B4FFF", 0.5), bgcolor: alpha("#6B4FFF", 0.07) },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#E0E7FF",
                fontSize: 13,
                lineHeight: 1.35,
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {opt.label}
            </Typography>
          </Button>
        ))}
      </Box>
    </Box>
  );
};


const deriveConfig = (ids: string[]): PanelConfig => {
  const set = new Set(ids);
  return {
    showChat: ["chat_room", "ai_chat_engagement", "ai_moderation"].some((id) =>
      set.has(id)
    ),
    showAI: ["ai_qa", "logistics_ai", "voice_ai", "smart_ads", "stream_understanding"].some(
      (id) => set.has(id)
    ),
    showShop: set.has("affiliate_store"),
    showQuiz: set.has("live_quizzes"),
    showAnnouncement:
      set.has("attendee_blasts") ||
      set.has("highlights") ||
      set.has("stream_understanding"),
    showAd: set.has("smart_ads"),
    showAiEngagement: set.has("ai_chat_engagement"),
    showModeration: set.has("ai_moderation"),
    showVoice: set.has("voice_ai"),
  };
};

const MessageCard: React.FC<{
  author: string;
  text: string;
  avatar: string;
  ai?: boolean;
}> = ({ author, text, avatar, ai }) => (
  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
    <AvatarBubble label={avatar} />
    <Box
      sx={{
        flex: 1,
        borderRadius: 3,
        border: `1px solid ${
          ai ? alpha("#8B5CF6", 0.45) : alpha("#FFFFFF", 0.1)
        }`,
        background: ai
          ? "linear-gradient(180deg, rgba(44, 28, 80, 0.75) 0%, rgba(23, 19, 50, 0.85) 100%)"
          : "linear-gradient(180deg, rgba(20, 24, 52, 0.85) 0%, rgba(14, 18, 40, 0.85) 100%)",
        backdropFilter: "blur(6px)",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.76), fontWeight: 700 }}>
        {author}
      </Typography>
      <Typography variant="body2" sx={{ color: "#FFFFFF", mt: 0.5, lineHeight: 1.6 }}>
        {text}
      </Typography>
    </Box>
  </Box>
);

const ModeratedMessageCard: React.FC<{ author: string; reason?: string }> = ({
  author,
  reason,
}) => (
  <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
    <AvatarBubble label={author.slice(0, 2).toUpperCase()} />
    <Box
      sx={{
        flex: 1,
        borderRadius: 3,
        border: `1px dashed ${alpha("#FFFFFF", 0.18)}`,
        background: alpha("#0F1420", 0.55),
        backdropFilter: "blur(4px)",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <GppGoodIcon sx={{ fontSize: 16, color: alpha("#A78BFA", 0.95) }} />
        <Typography variant="caption" sx={{ color: alpha("#FFFFFF", 0.65), fontWeight: 700 }}>
          Message removed by AI moderation
        </Typography>
      </Stack>
      {reason && (
        <Typography variant="caption" sx={{ mt: 0.75, color: alpha("#FFFFFF", 0.5) }}>
          {reason}
        </Typography>
      )}
    </Box>
  </Box>
);

const ComposerShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      borderRadius: 10,
      border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
      bgcolor: alpha("#13172F", 0.95),
      px: 2,
      py: 1.25,
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    {children}
  </Box>
);

const AiComposer: React.FC<{
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
}> = ({ placeholder, value, onChange, onSubmit, disabled }) => (
  <ComposerShell>
    <InputBase
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          onSubmit();
        }
      }}
      placeholder={placeholder}
      multiline
      maxRows={4}
      sx={{
        flex: 1,
        fontSize: 14,
        color: "#E0E7FF",
        "& .MuiInputBase-input::placeholder": {
          color: alpha("#FFFFFF", 0.45),
          opacity: 1,
        },
      }}
    />
    <Button size="small" sx={{ minWidth: 36, height: 32, borderRadius: 2, bgcolor: alpha("#6B4FFF", 0.15), color: "#C4B5FD" }}>
      <MicIcon fontSize="small" />
    </Button>
    <Button
      size="small"
      disabled={!value.trim() || disabled}
      onClick={onSubmit}
      sx={{
        minWidth: 44,
        height: 34,
        borderRadius: 999,
        px: 1.5,
        bgcolor: !value.trim() || disabled ? alpha("#6C47FF", 0.35) : "#6C47FF",
        color: "#FFFFFF",
        "&:hover": { bgcolor: disabled ? alpha("#6C47FF", 0.35) : "#5836E0" },
      }}
    >
      <SendRoundedIcon fontSize="small" />
    </Button>
  </ComposerShell>
);

const AdCard: React.FC<{ headline: string; subhead: string }> = ({ headline, subhead }) => (
  <Box sx={{ borderRadius: 3, border: `1px solid ${alpha("#FFFFFF", 0.12)}`, overflow: "hidden",
    background: "linear-gradient(135deg, rgba(255,180,162,0.2) 0%, rgba(255,99,132,0.35) 100%)" }}>
    <Box sx={{ height: 120, backgroundImage:
        "linear-gradient(135deg, rgba(255,180,162,0.6) 0%, rgba(255,99,132,0.75) 70%, rgba(255,214,102,0.6) 100%)",
        display: "flex", alignItems: "center", justifyContent: "space-between", px: 3 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ color: "#0D1022", fontWeight: 700 }}>
          {headline}
        </Typography>
        <Typography variant="body2" sx={{ color: alpha("#0D1022", 0.75), mt: 0.5 }}>
          {subhead}
        </Typography>
        <Button size="small" sx={{ mt: 1.5, borderRadius: 999, textTransform: "none", fontWeight: 600, bgcolor: "#0D1022", color: "#FFFFFF", px: 2 }}>
          Learn more
        </Button>
      </Box>
      <Box sx={{ width: 80, height: 80, borderRadius: "18px", bgcolor: "#FFFFFF",
        display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#0D1022" }}>QR</Box>
    </Box>
  </Box>
);

// put this near the top of the file (helpers)
function normalizeStreamQuizPayload(raw: any): { question: string; options: string[] } | null {
  // expected shapes:
  //  1) { quiz: { quiz: { question, options, correctAnswer } } }
  //  2) { quiz: { question, options } }
  //  3) { quiz: {} } or string -> ignore
  if (!raw || typeof raw !== "object") return null;

  let q = (raw as any).quiz;
  if (!q) return null;

  // if server wrapped once more: q.quiz
  if (q && typeof q === "object" && "quiz" in q) q = q.quiz;

  const question = q?.question;
  const options  = q?.options;

  if (typeof question === "string" && Array.isArray(options) && options.every(o => typeof o === "string")) {
    return { question, options };
  }
  return null;
}


/** -------------------- MAIN PANEL -------------------- */
const BoltOsImmersivePanel: React.FC<Props> = (props) => {
  const { features, activeIds, sessionKey, initialChat, initialAnnouncements, initialQuizzes } = props;


  const session = useMemo(() => sessionKey ?? "preview", [sessionKey]);
  const isPreview = !sessionKey;

  const [liveFeatures, setLiveFeatures] = useState<string[]>(() => Array.from(new Set(activeIds)));
  useEffect(() => {
    setLiveFeatures(Array.from(new Set(activeIds)));
  }, [activeIds]);

  const config = useMemo(() => deriveConfig(liveFeatures), [liveFeatures]);
  const [activeTab, setActiveTab] = useState(0);

  const editorRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const activationChips = useMemo(
    () =>
      liveFeatures
        .map((id) => features.find((f) => f.id === id))
        .filter((f): f is FeatureDefinition => Boolean(f)),
    [liveFeatures, features]
  );

  // chat state hydrated from backend
  const [messages, setMessages] = useState<DisplayChatMessage[]>(() =>
    normaliseChatMessages(initialChat),
  );

  const [hlsUrl, setHlsUrl] = useState<string | null>(null);


  useEffect(() => {
    if (!sessionKey) { setHlsUrl(null); return; }
    axios.get(`${API_BASE}/immersive-runtime/session/${sessionKey}`)
        .then((res) => {
        const media = res.data?.answers?.media_source;
        setHlsUrl(media || null);
        })
        .catch(() => setHlsUrl(null));
  }, [sessionKey]);

useEffect(() => {
  const hasHighlights = liveFeatures.includes("highlights");
  const hasListener   = liveFeatures.includes("stream_understanding");

  let ws: WebSocket | null = null;

  if (hlsUrl && hasHighlights && hasListener) {
    ws = new WebSocket(
      `${RUNTIME_WS_BASE}/ws/summary/${encodeURIComponent(hlsUrl)}`
    );

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        if (data?.summary && typeof data.summary === "string") {
          setAnnouncement({ text: data.summary, timestamp: Date.now() / 1000 });
        }
      } catch {
        /* ignore */
      }
    };

    ws.onerror = () => { /* ignore */ };
  }

  return () => {
    if (ws) ws.close();
  };
}, [hlsUrl, liveFeatures]);

// ───────── Live Quizzes (quiz) ─────────
useEffect(() => {
  const hasQuizzes  = liveFeatures.includes("live_quizzes");
  const hasListener = liveFeatures.includes("stream_understanding");

  let ws: WebSocket | null = null;

  if (hlsUrl && hasQuizzes && hasListener) {
    ws = new WebSocket(`${RUNTIME_WS_BASE}/ws/quiz/${encodeURIComponent(hlsUrl)}`);

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        const normalized = normalizeStreamQuizPayload(data);
        if (normalized) {
          // if you’re using the precedence helper setQuiz(..., "stream"), use that here:
          // setQuiz({ ...normalized, timestamp: Date.now() / 1000 }, "stream");
          setActiveQuiz({
            question: normalized.question,
            options: normalized.options,
            timestamp: Date.now() / 1000,
          });
        }
      } catch {
        /* ignore */
      }
    };

    ws.onerror = () => { /* ignore */ };
  }

  return () => {
    if (ws) ws.close();
  };
}, [hlsUrl, liveFeatures]);


  useEffect(() => {
    setMessages(normaliseChatMessages(initialChat));
  }, [initialChat]);
  const [announcement, setAnnouncement] = useState<RuntimeAnnouncement | null>(() =>
    initialAnnouncements && initialAnnouncements.length
      ? initialAnnouncements[initialAnnouncements.length - 1]
      : null,
  );
  const [defaultBannerDismissed, setDefaultBannerDismissed] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState<RuntimeQuiz | null>(() =>
    initialQuizzes && initialQuizzes.length ? initialQuizzes[initialQuizzes.length - 1] : null,
  );
  const shouldShowQuiz = Boolean(activeQuiz) || (isPreview && config.showQuiz);
  useEffect(() => {
    if (initialAnnouncements && initialAnnouncements.length) {
      setAnnouncement(initialAnnouncements[initialAnnouncements.length - 1]);
    }
  }, [initialAnnouncements]);

  useEffect(() => {
    if (announcement) {
      setDefaultBannerDismissed(false);
    }
  }, [announcement]);

    useEffect(() => {
    // only re-enable preview banner automatically when actually in preview
    if (!announcement && isPreview && config.showAnnouncement) {
        setDefaultBannerDismissed(false);
    }
    }, [announcement, config.showAnnouncement, isPreview]);


  useEffect(() => {
    if (initialQuizzes && initialQuizzes.length) {
      setActiveQuiz(initialQuizzes[initialQuizzes.length - 1]);
    }
  }, [initialQuizzes]);


  const appendMessageUnique = useCallback((message: RuntimeChatMessage) => {
    setMessages((prev) => {
      const alreadyExists = prev.some((existing) =>
        existing.author === message.author &&
          existing.text === message.text &&
          Math.abs((existing.timestamp ?? 0) - (message.timestamp ?? 0)) < 1e-3,
      );
      if (alreadyExists) {
        return prev;
      }
      return [...prev, { ...message }];
    });
  }, []);

  useEffect(() => {
    if (!sessionKey) return () => {};
    const socket = new WebSocket(`${API_BASE.replace(/^http/, "ws")}/immersive-runtime/session/${sessionKey}/events`);

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        switch (payload.type) {
          case "session_snapshot":
            if (Array.isArray(payload.features)) {
              setLiveFeatures(Array.from(new Set(payload.features)));
            }
            if (Array.isArray(payload.announcements) && payload.announcements.length) {
              setAnnouncement(payload.announcements[payload.announcements.length - 1]);
            }
            if (Array.isArray(payload.quizzes) && payload.quizzes.length) {
              setActiveQuiz(payload.quizzes[payload.quizzes.length - 1]);
            }
            break;
          case "features":
            if (Array.isArray(payload.features)) {
              setLiveFeatures(Array.from(new Set(payload.features)));
            }
            break;
          case "announcement":
            if (payload.announcement) {
              setAnnouncement(payload.announcement);
            }
            break;
          case "quiz":
            if (payload.quiz) {
              setActiveQuiz(payload.quiz);
            }
            break;
          case "chat_message":
            if (payload.message) {
              appendMessageUnique(payload.message);
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.warn("Malformed session event", error);
      }
    };

    socket.onerror = () => {
      console.warn("Session events websocket disconnected");
    };

    return () => {
      socket.close();
    };
  }, [sessionKey, appendMessageUnique]);


  const [draft, setDraft] = useState("");
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);

  const [aiMessages, setAiMessages] = useState<AiMessage[]>([
    {
      id: "intro",
      role: "assistant",
      text: "Hi there! I'm IRIS. Ask about key moments, summaries, or what's happening live.",
    },
  ]);
  const [aiDraft, setAiDraft] = useState("");
  const [aiIsLoading, setAiIsLoading] = useState(false);
  const aiEndRef = useRef<HTMLDivElement | null>(null);

  const handleInput = (event: React.FormEvent<HTMLDivElement>) => {
    setDraft(event.currentTarget.textContent ?? "");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      postMessage().catch(() => {});
    }
  };

  const postMessage = async (): Promise<void> => {
    const text = (editorRef.current?.textContent ?? "").trim();
    if (!text) return;

    const blocked = /email|phone|address/i.test(text);
    if (blocked) {
      setMessages((prev) => [
        ...prev,
        {
          author: "You",
          text,
          avatar: "YO",
          __moderated: true,
          reason: "Possible personal info. Please keep it safe & on-topic.",
          timestamp: Date.now() / 1000,
        },
      ]);
      setDraft("");
      if (editorRef.current) {
        editorRef.current.textContent = "";
      }
      return;
    }

    setDraft("");
    if (editorRef.current) {
      editorRef.current.textContent = "";
    }

    const baseMessage: RuntimeChatMessage = {
      author: "You",
      text,
      avatar: "YO",
      timestamp: Date.now() / 1000,
    };

    if (!sessionKey) {
      appendMessageUnique(baseMessage);
      return;
    }

    try {
      const response = await axios.post<{ message: RuntimeChatMessage }>(
        `${API_BASE}/immersive-runtime/session/${sessionKey}/chat`,
        baseMessage,
      );
      if (response.data?.message) {
        appendMessageUnique(response.data.message);
      }
    } catch (error) {
      console.error("Failed to persist chat message", error);
      appendMessageUnique(baseMessage);
    }
  };

  const openEmojiPicker = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchor(event.currentTarget);
  };

  const closeEmojiPicker = () => setEmojiAnchor(null);

  const appendEmoji = (emoji: string) => {
    const node = editorRef.current;
    if (!node) return;
    const nextValue = `${node.textContent ?? ""}${emoji}`;
    node.textContent = nextValue;
    setDraft(nextValue);
    requestAnimationFrame(() => {
      const selection = window.getSelection();
      if (!selection) return;
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    });
    node.focus();
    closeEmojiPicker();
  };

  const handleAiSend = (): void => {
    const trimmed = aiDraft.trim();
    if (!trimmed || aiIsLoading) return;

    const userMessage: AiMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: trimmed,
    };
    const history = [...aiMessages, userMessage];
    setAiMessages(history);
    setAiDraft("");

    if (!sessionKey) {
      setAiMessages([
        ...history,
        {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          text: "IRIS preview is offline without a session key. Generate a panel first to enable the assistant.",
        },
      ]);
      return;
    }

    setAiIsLoading(true);
    const payloadMessages = history
      .slice(-10)
      .map(({ role, text }) => ({ role, text }));

    axios
      .post<{ reply: string }>(`${API_BASE}/immersive-runtime/session/${sessionKey}/ai`, {
        messages: payloadMessages,
      })
      .then((response) => {
        const replyText = response.data?.reply || "IRIS is thinking...";
        setAiMessages((prev) => [
          ...prev,
          { id: `assistant-${Date.now()}`, role: "assistant", text: replyText },
        ]);
      })
      .catch((error) => {
        console.error("IRIS preview error", error);
        setAiMessages((prev) => [
          ...prev,
          {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            text: "IRIS is offline right now, but we'll sync the brain soon.",
          },
        ]);
      })
      .finally(() => setAiIsLoading(false));
  };

  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiMessages, aiIsLoading]);


  
    const bannerText =
    announcement?.text ||
    (isPreview && !defaultBannerDismissed && config.showAnnouncement
        ? DEFAULT_ANNOUNCEMENT_COPY
        : null);

    const handleAnnouncementClose = () => {
    if (announcement) setAnnouncement(null);
    else if (isPreview) setDefaultBannerDismissed(true); // only matters in preview
    };

  const ChatTab = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {bannerText && (
        <StreamSummaryBanner
          summary={bannerText}
          hlsUrl="preview://immersive"
          onClose={handleAnnouncementClose}
          sx={{ position: "static", width: "100%", px: 0, transform: "none", boxShadow: "none", borderRadius: 3 }}
        />
      )}

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, pt: 2, pb: 1, ...SCROLLBAR_SX }}>
        {config.showAd && (
          <AdCard headline="Earn up to $480/hr" subhead="Share your expertise in live sessions and grow your community." />
        )}

        {config.showChat ? (
          <Stack spacing={1.25} sx={{ mt: config.showAd ? 2 : 0 }}>
            {messages.map((m, i) =>
              m.__moderated ? (
                <ModeratedMessageCard key={`m-${i}`} author={m.author} reason={m.reason} />
              ) : (
                <MessageCard key={`m-${i}`} author={m.author} text={m.text} avatar={m.avatar} ai={m.ai} />
              )
            )}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ mt: 2, color: alpha("#FFFFFF", 0.5) }}>
            Live chat is turned off.
          </Typography>
        )}
      </Box>

        {shouldShowQuiz && (
        <Box sx={{ px: 3, pb: 1 }}>
            <QuizCard quiz={activeQuiz} />
        </Box>
        )}

      <Box sx={{ px: 3, pb: 3 }}>
        {config.showChat ? (
          <>
            <Box
              sx={{
                borderRadius: 10,
                border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
                bgcolor: alpha("#13172F", 0.95),
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box sx={{ position: "relative", flex: 1 }}>
                <Box
                  ref={editorRef}
                  role="textbox"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleInput}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  sx={{
                    outline: "none",
                    minHeight: 22,
                    color: "#E0E7FF",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                />
                {!draft && !focused && !editorRef.current?.textContent && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      pointerEvents: "none",
                      color: alpha("#FFFFFF", 0.45),
                    }}
                  >
                    Type something...
                  </Box>
                )}
              </Box>

              <Button
                size="small"
                onClick={openEmojiPicker}
                sx={{ minWidth: 36, height: 32, borderRadius: 2, bgcolor: alpha("#6B4FFF", 0.15), color: "#C4B5FD" }}
              >
                <EmojiEmotionsIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                sx={{ minWidth: 44, height: 34, borderRadius: 999, px: 1.5, bgcolor: "#6C47FF", color: "#FFFFFF" }}
                onClick={() => { postMessage().catch(() => {}); }}
              >
                <SendRoundedIcon fontSize="small" />
              </Button>
            </Box>
            <Popover
              open={Boolean(emojiAnchor)}
              anchorEl={emojiAnchor}
              onClose={closeEmojiPicker}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              PaperProps={{
                sx: {
                  bgcolor: alpha("#0B0F24", 0.95),
                  borderRadius: 3,
                  border: `1px solid ${alpha("#FFFFFF", 0.12)}`,
                  p: 1,
                  boxShadow: "0 18px 40px rgba(8,10,25,0.6)",
                },
              }}
            >
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", maxWidth: 220 }}>
                {EMOJI_CHOICES.map((emoji) => (
                  <Button
                    key={emoji}
                    size="small"
                    onClick={() => appendEmoji(emoji)}
                    sx={{
                      minWidth: 0,
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      fontSize: 20,
                      lineHeight: 1,
                      textTransform: "none",
                    }}
                  >
                    {emoji}
                  </Button>
                ))}
              </Stack>
            </Popover>
          </>
        ) : (
          <Chip
            label="Live chat is off"
            size="small"
            sx={{
              borderRadius: 2,
              color: "#C7D2FE",
              bgcolor: alpha("#6366F1", 0.18),
              border: `1px solid ${alpha("#6366F1", 0.35)}`,
              fontWeight: 700,
            }}
          />
        )}
      </Box>
    </Box>
  );

  const AiTab = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          ...SCROLLBAR_SX,
        }}
      >
        <Box
          sx={{
            borderRadius: 4,
            border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
            bgcolor: alpha("#13172F", 0.95),
            p: 3,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(107, 79, 255, 0.4), rgba(138, 85, 255, 0.8))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
              IRIS - Your Stream Companion
            </Typography>
            <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.65), mt: 1 }}>
              Ask anything about the live stream, surface quick summaries, or get AI to track key moments for you.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1.5}
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            sx={{ gap: 1, width: "100%" }}
          >
            {["What's happening now?", "Summarize the stream", "Key moments"].map((label) => (
              <Button
                key={label}
                variant="outlined"
                onClick={() => setAiDraft(label)}
                sx={{
                  borderRadius: 999,
                  borderColor: alpha("#FFFFFF", 0.15),
                  color: "#E0E7FF",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  "&:hover": { borderColor: alpha("#8B5CF6", 0.5) },
                }}
              >
                {label}
              </Button>
            ))}
          </Stack>
        </Box>

        <Stack spacing={1.5}>
          {aiMessages.map(({ id, role, text }) => (
            <AiMessageBubble key={id} messageRole={role} text={text} />
          ))}
          {aiIsLoading && (
            <AiMessageBubble messageRole="assistant" text="IRIS is crafting a preview response..." loading />
          )}
          <Box ref={aiEndRef} />
        </Stack>
      </Box>
      <Box sx={{ px: 3, pb: 3 }}>
        <AiComposer
          placeholder="Ask anything..."
          value={aiDraft}
          onChange={setAiDraft}
          onSubmit={handleAiSend}
          disabled={aiIsLoading}
        />
      </Box>
    </Box>
  );

  const ShopTab = (
    <Box sx={{ height: "100%", overflowY: "auto", px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
        Featured drops
      </Typography>
      {[1, 2].map((idx) => (
        <Box key={idx} sx={{ borderRadius: 18, border: `1px solid ${alpha("#FFFFFF", 0.1)}`, bgcolor: alpha("#141834", 0.96), overflow: "hidden" }}>
          <Box sx={{ height: 140, background: "linear-gradient(135deg, rgba(99,102,241,0.3) 0%, rgba(139,92,246,0.45) 100%)" }} />
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
              Creator summit hoodie
            </Typography>
            <Typography variant="body2" sx={{ color: alpha("#FFFFFF", 0.65), lineHeight: 1.6 }}>
              Limited-run drop with stitched sleeve detailing and an NFC tag for exclusive content.
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ color: "#C4B5FD", fontWeight: 700 }}>
                $68.00
              </Typography>
              <Button variant="contained" size="small" sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#6C47FF", "&:hover": { bgcolor: "#5A3AE0" } }}>
                View
              </Button>
            </Stack>
          </Box>
        </Box>
      ))}
    </Box>
  );

// replace your current tabs typing with this:
const tabs = useMemo<Array<{
  id: string;
  label: string;
  icon: React.ReactElement;   // ?? not ReactNode
  content: React.ReactNode;
}>>(() => {
  const arr = [
    { id: "chat", label: config.showChat ? "Chat" : "Main", icon: <ChatBubbleIcon fontSize="small" />, content: ChatTab },
  ];
  if (config.showAI)  arr.push({ id: "ai",   label: "AI",   icon: <AutoAwesomeIcon fontSize="small" />, content: AiTab });
  if (config.showShop)arr.push({ id: "shop", label: "Shop", icon: <LocalMallIcon  fontSize="small" />, content: ShopTab });
  return arr;
}, [config, ChatTab, AiTab, ShopTab]);

  return (
    <Box
      data-session={session} 
      sx={{
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        display: "flex",
        flexDirection: "column",
        borderRadius: 24,
        overflow: "hidden",
        border: `1px solid ${alpha("#FFFFFF", 0.08)}`,
        background: "linear-gradient(180deg, #070b1e 0%, #050716 100%)",
        boxShadow: "0 28px 52px rgba(5,7,22,0.6)",
      }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, background: "linear-gradient(180deg, rgba(15,18,42,0.95) 0%, rgba(9,12,28,0.9) 100%)" }}>
        <Tabs
          value={Math.min(activeTab, tabs.length - 1)}
          onChange={(_, n) => setActiveTab(n)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{ ".MuiTabs-flexContainer": { gap: 8 } }}
        >
          {tabs.map((t) => (
            <HeaderTab key={t.id} icon={t.icon} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <Divider sx={{ borderColor: alpha("#FFFFFF", 0.08) }} />
      <Box sx={{ flex: 1, overflow: "hidden" }}>{tabs[Math.min(activeTab, tabs.length - 1)]?.content}</Box>
    </Box>
  );
};

export default BoltOsImmersivePanel;
